import { z } from "zod";
import { latitudeSchema, longitudeSchema } from "./common";
import { PROVINCE_CODES } from "../catalogs/locations";

// Google-Maps URL parser. Cheaper than a paid geocoder (no API key, no
// per-request cost) and exact, because lat/lng + street/district/city all
// come straight from Google's own URL — we just unwrap them.
//
// Two URL shapes the user might paste:
//
//   1. Short link from the mobile share sheet:
//        https://maps.app.goo.gl/abc123
//      The server has to follow one HTTP redirect to get the long URL.
//
//   2. Long link from desktop or after the redirect:
//        https://www.google.com/maps/place/<URL-encoded address>/@lat,lng,zoom/...
//      Address is between `/place/` and the next `/`, lat/lng follow the `@`.

export const geocodeParseUrlRequestSchema = z.object({
  url: z.string().url().max(2048),
});

export const geocodeParsedAddressSchema = z.object({
  // Best-effort split of the comma-separated path Google embeds in `/place/`.
  // For street-level pins these usually populate; for landmarks (parks, malls
  // referred to by name) only `address` is filled and the rest stay null.
  address: z.string(),
  district: z.string().nullable(),
  city: z.string().nullable(),
  lat: latitudeSchema,
  lng: longitudeSchema,
  // Resolved province from the SUPPORTED_PROVINCES allowlist. null means
  // the GPS falls outside any supported market — the client must block
  // the address with an out-of-region message instead of populating it.
  provinceCode: z.enum(PROVINCE_CODES).nullable(),
  provinceName: z.string().nullable(),
  // ─── 2025 post-merger ward, resolved server-side by point-in-polygon on
  // lat/lng (WardsService) — NOT from the geocoder's stale admin names.
  // `wardProvinceCode` is the official NEW 34-province numeric code as a string
  // ("79" = HCM), distinct from the legacy `provinceCode` enum above. All null
  // when the point falls outside every ward polygon (user then picks manually).
  wardCode: z.string().nullable(),
  wardName: z.string().nullable(),
  wardProvinceCode: z.string().nullable(),
  wardProvinceName: z.string().nullable(),
  // The fully-resolved Google Maps URL, after any short-link redirect.
  resolvedUrl: z.string().url(),
});

export type GeocodeParseUrlRequest = z.infer<
  typeof geocodeParseUrlRequestSchema
>;
export type GeocodeParsedAddress = z.infer<typeof geocodeParsedAddressSchema>;

// ── Address autocomplete (Goong) ────────────────────────────────────────────
//
// Typeahead address search that avoids the "go to Google Maps → Share → paste"
// dance. Backed by Goong (goong.io) — a VN-focused maps provider with strong
// local address data and none of Google Maps Platform's separate-billing hoops.
// Two hops, both proxied through our API so the key never touches the browser:
//
//   1. autocomplete(input, sessionToken) → a short list of suggestions.
//   2. placeDetails(placeId, sessionToken) → the picked place resolved into the
//      SAME GeocodeParsedAddress shape the URL parser returns, so the picker's
//      onChange handler is identical for both flows.
//
// `sessionToken` is a client-generated UUID that groups the keystrokes of one
// search + its final details lookup into a single billing session (Goong, like
// Google, charges per session when a token is threaded through).

export const geocodeAutocompleteRequestSchema = z.object({
  // Trimmed on the client; server also trims. Empty input is rejected before
  // we ever hit Google — no point paying for a blank query.
  input: z.string().trim().min(1).max(256),
  // Client-generated UUID (crypto.randomUUID). Optional so a caller may omit
  // it, but strongly recommended — it's what makes billing per-session.
  sessionToken: z.string().min(1).max(128).optional(),
});

export const geocodeSuggestionSchema = z.object({
  // Opaque Google place id, fed back into placeDetails.
  placeId: z.string(),
  // "395 Bùi Đình Túy" — the bold main line in Google's UI.
  primaryText: z.string(),
  // "Phường 12, Bình Thạnh, TP.HCM" — the greyed context line. May be empty
  // for very short/ambiguous matches.
  secondaryText: z.string(),
  // Full "primaryText, secondaryText" for a single-line render fallback.
  fullText: z.string(),
});

export const geocodeAutocompleteResponseSchema = z.object({
  suggestions: z.array(geocodeSuggestionSchema),
});

export const geocodePlaceDetailsRequestSchema = z.object({
  placeId: z.string().min(1).max(512),
  sessionToken: z.string().min(1).max(128).optional(),
});

export type GeocodeAutocompleteRequest = z.infer<
  typeof geocodeAutocompleteRequestSchema
>;
export type GeocodeSuggestion = z.infer<typeof geocodeSuggestionSchema>;
export type GeocodeAutocompleteResponse = z.infer<
  typeof geocodeAutocompleteResponseSchema
>;
export type GeocodePlaceDetailsRequest = z.infer<
  typeof geocodePlaceDetailsRequestSchema
>;
